/**
 * Compositor de mensajes: texto libre (Enter envia, Shift+Enter salta de linea)
 * o plantilla aprobada cuando la ventana de 24 h esta cerrada.
 *
 * En el modo plantilla ya no se escribe el nombre a mano: se elige de un
 * desplegable con las plantillas **aprobadas** de la cuenta de la conversacion.
 * Escribirlo mal era la causa del error 132001 ("la plantilla no existe"), y
 * fuera de la ventana de 24 h no hay ninguna otra forma de responder.
 */
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent, KeyboardEvent, ReactElement } from 'react';
import type { SendMessageRequest, TemplateView } from '@shared/api-types.ts';
import { MAX_TEXT_LENGTH } from '@domain/types.ts';
import { api, errorMessage, isAbortError } from '../api.ts';
import { navigate } from '../hooks.ts';
import {
  TEMPLATE_HEADER_FORMAT_LABELS,
  buildTemplateSendComponents,
  parseTemplate,
} from '../template-utils.ts';
import { ErrorBanner, Field, SegmentedControl, Spinner } from './Feedback.tsx';
import { Icon } from './Icon.tsx';

type Mode = 'text' | 'template';

type TemplatesState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; templates: TemplateView[] }
  | { status: 'error'; message: string };

export function Composer({
  accountId,
  windowOpen,
  windowLabel,
  sending,
  templateRequired,
  onSend,
  onDismissTemplateRequired,
}: {
  /** Numero de la conversacion: sus plantillas aprobadas son las que se ofrecen. */
  accountId: string;
  windowOpen: boolean;
  windowLabel: string;
  sending: boolean;
  /** Mensaje del backend cuando la ventana se ha cerrado (`requiresTemplate`). */
  templateRequired: string | null;
  onSend: (request: SendMessageRequest) => Promise<boolean>;
  onDismissTemplateRequired: () => void;
}): ReactElement {
  const [mode, setMode] = useState<Mode>('text');
  const [text, setText] = useState('');
  const [templatesState, setTemplatesState] = useState<TemplatesState>({ status: 'idle' });
  const [templatesReload, setTemplatesReload] = useState(0);
  const [selectedId, setSelectedId] = useState('');
  const [headerValues, setHeaderValues] = useState<Record<number, string>>({});
  const [bodyValues, setBodyValues] = useState<Record<number, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Si el backend responde 409 con `requiresTemplate`, se ofrece el formulario.
  useEffect(() => {
    if (templateRequired) {
      setMode('template');
      setFormError(null);
    }
  }, [templateRequired]);

  // Las plantillas se piden al abrir el modo plantilla para la cuenta de la
  // conversacion. Se conservan al volver al texto libre y se refrescan cada vez
  // que se reabre, para no ofrecer una plantilla que Meta acaba de suspender.
  useEffect(() => {
    if (mode !== 'template' || !accountId) return;
    const controller = new AbortController();
    setTemplatesState((prev) => (prev.status === 'ready' ? prev : { status: 'loading' }));

    api
      .listTemplates({ accountId, status: 'APPROVED' }, controller.signal)
      .then((templates) => {
        if (!controller.signal.aborted) setTemplatesState({ status: 'ready', templates });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setTemplatesState({ status: 'error', message: errorMessage(error) });
      });

    return () => controller.abort();
  }, [accountId, mode, templatesReload]);

  const approvedTemplates = templatesState.status === 'ready' ? templatesState.templates : [];
  const selected = approvedTemplates.find((template) => template.id === selectedId) ?? null;
  const parsed = useMemo(() => (selected ? parseTemplate(selected) : null), [selected]);

  // Si la plantilla elegida desaparece al recargar, se vuelve a pedir una.
  useEffect(() => {
    if (templatesState.status !== 'ready') return;
    if (selectedId && !templatesState.templates.some((template) => template.id === selectedId)) {
      setSelectedId('');
    }
  }, [selectedId, templatesState]);

  // Cada plantilla tiene sus propias variables: al cambiar, se vacian.
  useEffect(() => {
    setHeaderValues({});
    setBodyValues({});
    setFormError(null);
  }, [selectedId]);

  const textTooLong = text.length > MAX_TEXT_LENGTH;
  const canSendText = windowOpen && !sending && text.trim().length > 0 && !textTooLong;
  const headerVariables =
    parsed && parsed.header?.format === 'TEXT' ? parsed.header.variables : [];
  const bodyVariables = parsed ? parsed.body.variables : [];

  const submitText = async (): Promise<void> => {
    if (!canSendText) return;
    const value = text.trim();
    const sent = await onSend({ kind: 'text', text: value });
    if (sent) setText('');
  };

  const submitTemplate = async (): Promise<void> => {
    if (!selected || !parsed) {
      setFormError('Elige una plantilla aprobada de la lista.');
      return;
    }

    const missingHeader = headerVariables.some((position) => !headerValues[position]?.trim());
    const missingBody = bodyVariables.some((position) => !bodyValues[position]?.trim());
    if (missingHeader || missingBody) {
      setFormError(
        'Rellena todos los valores: WhatsApp sustituye las variables {{1}}, {{2}}... por ellos.',
      );
      return;
    }

    setFormError(null);
    const components = buildTemplateSendComponents(parsed, headerValues, bodyValues);
    const request: SendMessageRequest =
      components.length > 0
        ? {
            kind: 'template',
            templateName: selected.name,
            language: selected.language,
            components,
          }
        : { kind: 'template', templateName: selected.name, language: selected.language };

    const sent = await onSend(request);
    if (sent) {
      setSelectedId('');
      setHeaderValues({});
      setBodyValues({});
    }
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (mode === 'text') void submitText();
    else void submitTemplate();
  };

  const handleTextKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    void submitText();
  };

  const templatesLoading = templatesState.status === 'idle' || templatesState.status === 'loading';

  return (
    <form className="composer" onSubmit={handleFormSubmit}>
      <div className="composer__top">
        <SegmentedControl<Mode>
          ariaLabel="Tipo de mensaje"
          value={mode}
          onChange={(value) => {
            setMode(value);
            setFormError(null);
          }}
          options={[
            { value: 'text', label: 'Texto libre' },
            { value: 'template', label: 'Plantilla' },
          ]}
        />
        <span className={windowOpen ? 'windowpill windowpill--open' : 'windowpill windowpill--closed'}>
          <Icon name="clock" />
          {windowOpen ? `Ventana de 24 h: ${windowLabel}` : `Ventana de 24 h cerrada (${windowLabel})`}
        </span>
      </div>

      {templateRequired ? (
        <div className="banner banner--warning" role="alert">
          <Icon name="warning" className="banner__icon" />
          <p className="banner__text">{templateRequired}</p>
          <button type="button" className="button button--ghost button--sm" onClick={onDismissTemplateRequired}>
            Entendido
          </button>
        </div>
      ) : null}

      {!windowOpen ? (
        <div className="banner banner--error" role="alert">
          <Icon name="error" className="banner__icon" />
          <p className="banner__text">
            La ventana de 24 h esta cerrada, asi que WhatsApp solo acepta plantillas aprobadas. Elige
            «Plantilla» para responder al cliente.
          </p>
        </div>
      ) : null}

      {mode === 'text' ? (
        <>
          <textarea
            className="composer__input"
            placeholder={
              windowOpen
                ? 'Escribe una respuesta… (Enter para enviar, Shift+Enter para salto de linea)'
                : 'No se puede escribir texto libre: la ventana de 24 h esta cerrada'
            }
            value={text}
            rows={3}
            disabled={!windowOpen || sending}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={handleTextKeyDown}
            aria-label="Mensaje de texto"
          />
          <div className="composer__bottom">
            <span className={textTooLong ? 'counter counter--error' : 'counter'}>
              {text.length} / {MAX_TEXT_LENGTH}
              {textTooLong ? ' · demasiado largo' : ''}
            </span>
            <button type="submit" className="button button--primary" disabled={!canSendText}>
              {sending ? <Spinner label="Enviando…" /> : (<><Icon name="send" />Enviar</>)}
            </button>
          </div>
        </>
      ) : (
        <div className="composer__template">
          {templatesLoading ? <Spinner label="Cargando plantillas aprobadas…" /> : null}

          {templatesState.status === 'error' ? (
            <ErrorBanner
              message={templatesState.message}
              onRetry={() => setTemplatesReload((token) => token + 1)}
            />
          ) : null}

          {templatesState.status === 'ready' && approvedTemplates.length === 0 ? (
            <div className="banner banner--warning" role="alert">
              <Icon name="warning" className="banner__icon" />
              <p className="banner__text">
                Este numero no tiene ninguna plantilla aprobada por Meta, asi que ahora mismo no se
                le puede escribir fuera de la ventana de 24 h. Crea una plantilla y espera su
                aprobacion.
              </p>
              <button
                type="button"
                className="button button--ghost button--sm"
                onClick={() => navigate({ name: 'templates' })}
              >
                <Icon name="templates" />
                Ir a Plantillas
              </button>
            </div>
          ) : null}

          {templatesState.status === 'ready' && approvedTemplates.length > 0 ? (
            <>
              <Field
                label="Plantilla aprobada"
                htmlFor="template-select"
                required
                hint="Solo se pueden enviar las plantillas que Meta ya ha aprobado."
              >
                <select
                  id="template-select"
                  className="select"
                  value={selectedId}
                  onChange={(event) => setSelectedId(event.target.value)}
                  disabled={sending}
                >
                  <option value="">Elige una plantilla</option>
                  {approvedTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.language})
                    </option>
                  ))}
                </select>
              </Field>

              {parsed ? (
                <div className="tpl-preview">
                  <p className="tpl-preview__label">Lo que se va a enviar</p>
                  {parsed.header ? (
                    <p className="tpl-preview__text">
                      {parsed.header.text
                        ? parsed.header.text
                        : `[Cabecera: ${TEMPLATE_HEADER_FORMAT_LABELS[parsed.header.format]}]`}
                    </p>
                  ) : null}
                  <p className="tpl-preview__text">
                    {parsed.body.text || 'Esta plantilla no tiene cuerpo de texto.'}
                  </p>
                  {parsed.footer ? <p className="tpl-preview__footer">{parsed.footer}</p> : null}
                  {parsed.buttons.length > 0 ? (
                    <div className="tpl-preview__buttons">
                      {parsed.buttons.map((button, index) => (
                        <span className="tpl-preview__button" key={`${button.type}-${index}`}>
                          {button.text || '(boton sin texto)'}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {headerVariables.length > 0 ? (
                <div className="grid grid--3">
                  {headerVariables.map((position) => (
                    <Field
                      key={`header-${position}`}
                      label={`Cabecera {{${position}}}`}
                      htmlFor={`composer-header-${position}`}
                      required
                    >
                      <input
                        id={`composer-header-${position}`}
                        className="input"
                        value={headerValues[position] ?? ''}
                        onChange={(event) =>
                          setHeaderValues((prev) => ({ ...prev, [position]: event.target.value }))
                        }
                        disabled={sending}
                      />
                    </Field>
                  ))}
                </div>
              ) : null}

              {bodyVariables.length > 0 ? (
                <div className="grid grid--3">
                  {bodyVariables.map((position) => (
                    <Field
                      key={`body-${position}`}
                      label={`Cuerpo {{${position}}}`}
                      htmlFor={`composer-body-${position}`}
                      required
                    >
                      <input
                        id={`composer-body-${position}`}
                        className="input"
                        value={bodyValues[position] ?? ''}
                        onChange={(event) =>
                          setBodyValues((prev) => ({ ...prev, [position]: event.target.value }))
                        }
                        disabled={sending}
                      />
                    </Field>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}

          {formError ? (
            <div className="banner banner--error" role="alert">
              <Icon name="error" className="banner__icon" />
              <p className="banner__text">{formError}</p>
            </div>
          ) : null}

          <div className="composer__bottom">
            <span className="counter">
              {selected
                ? `Se enviara «${selected.name}» en ${selected.language}.`
                : 'Solo plantillas aprobadas por Meta.'}
            </span>
            <button type="submit" className="button button--primary" disabled={sending || !selected}>
              {sending ? <Spinner label="Enviando…" /> : (<><Icon name="send" />Enviar plantilla</>)}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
